import * as sentry from '../../src/utils/sentry'
import { timeout } from '../../../shared/lib/timeout.no-deps'
import { globalHive } from '../../src/modules/hive'
import { setLastSyncTime } from '../../src/redux/actions/common'
import { syncAll } from '../../src/redux/actions/entities'
import { applyTheme } from '../../src/utils/theme'
import { Acti_initialise } from '../Acti/Acti.initialise'
import { Auth_cleanse } from './Auth.cleanse'
import { Devi_inactivityCheck } from '../Devi/Devi.inactivity'
import { Auth_getStatus } from './Auth.State'
import type { Base_Bus } from '../Base/Base.Bus'
import { Cust_hasSingleLocation, Cust_serverPrefsFetch } from '../Cust/Cust.serverPrefs'
import { Cust_sync } from '../Cust/Cust.sync'
import { DS_getData, DS_setData, type DS_TData } from '../DS/DS.Data'
import { E2E_dispatchSyncSuccess } from '../E2E/E2E.dispatch'
import { Form_sync } from '../Form/Form.sync'
import { I18N_sync } from '../I18N/I18N.sync'
import { IDB_lastActivity } from '../IDB/IDB.dexie'
import { IDB_actionTypesWrite } from '../IDB/IDB.table.actionType'
import { IDB_calendarEventsWrite } from '../IDB/IDB.table.calendarEvent'
import { IDB_fixesWrite } from '../IDB/IDB.table.fix'
import { IDB_issuesWrite } from '../IDB/IDB.table.issue'
import { IDB_locationsWrite } from '../IDB/IDB.table.location'
import { IDB_locationFocusListsWrite } from '../IDB/IDB.table.locationFocusList'
import { IDB_proceduresWrite } from '../IDB/IDB.table.procedure'
import { IDB_reportsWrite } from '../IDB/IDB.table.report'
import { IDB_smartDatasWrite } from '../IDB/IDB.table.smartData'
import { IDB_smartDataOrderingsWrite } from '../IDB/IDB.table.smartDataOrdering'
import { IDB_smartDataReferencesWrite } from '../IDB/IDB.table.smartDataReference'
import { IDB_unitsWrite } from '../IDB/IDB.table.unit'
import { IDB_unitListsWrite } from '../IDB/IDB.table.unitList'
import { IDB_unitTypesWrite } from '../IDB/IDB.table.unitType'
import { Imag_cacheImages } from '../Imag/Imag.cache'
import { Imag_getImageUrlsForUnits } from '../Imag/Imag.getUrls'
import { Loca_fetchTimelineEvents } from '../Loca/Loca.fetchTimelineEvents'
import { Loca_updateCurrentTimeline } from '../Loca/Loca.updateCurrentTimeline'
import { Logg_event } from '../Logg/Logg.event'
import { Logg_timer } from '../Logg/Logg.timer'
import { LS_accessTokenSet } from '../LS/LS.accessToken'
import { LS_customerGet, LS_customerSet } from '../LS/LS.customer'
import { LS_serverPrefsSet } from '../LS/LS.serverPrefs'
import { LS_userGet, LS_userSet } from '../LS/LS.user'
import { Noti_subscribe } from '../Noti/Noti.subscribe'
import { Post_sync } from '../Post/Post.sync'
import { Rout_toHome } from '../Rout/Rout.home'
import { SD_extractSmartDataItems } from '../SD/SD.extractSmartDataItems'
import { SD_extractTargetReferences } from '../SD/SD.targets'
import { Serv_send } from '../Serv/Serv.socketClient'
import { Sync_reset } from '../Sync/Sync.initialise'
import { Sync_syncProcessFlow } from '../Sync/Sync.processFlow'
import { Sync_syncCommon } from '../Sync/Sync.syncCommon'
import { Todo_sync } from '../Todo/Todo.sync'
import { User_fetch } from '../User/User.fetch'
import type { User_IUser } from '../User/User.IUser'
import * as UserProfileImageCache from '../User/UserProfileImageCache'
import { Util_unixTime } from '../Util/Util.date'
import { retry } from '../../../shared/lib/retry.no-deps'
import { I18N_setLocale } from '../I18N/I18N.locale'
import { Devi_recoverInProgressData } from '../Devi/Devi.InProgressData'
import { updateLastActiveTime } from '../Devi/Devi.recentUsers'
import { Logg_createLogger } from '../Logg/Logg.Logger'
import { Auth_pendoIdentify } from '../Auth/Auth.pendo'
import { Translations } from '../../../shared/i18n/Translations'
import * as LaunchDarkly from '../Util/Util.launchdarkly'
import * as ZendeskWebChat from '../../../app-core/lib/util/zendeskWebChat'
import '@sentry/tracing'
import * as Sentry from '@sentry/browser'

export async function Auth_loginSync(bus: Base_Bus) {
    const logger = Logg_createLogger('Auth_loginSync')
    const sentryTrnx = Sentry.startTransaction({ name: 'Auth_loginSync' })

    try {
        Logg_event('Start', { label: 'LoginSync' })
        const timer = Logg_timer()

        // Start the spinner
        DS_setData('LoginSyncInitiated', () => ({ auth: { status: 'syncing' } }))

        // takingAWhile timeout to apologize to the user
        timeout(4 * 1000).then(() => {
            if (Auth_getStatus() !== 'syncing') return

            DS_setData('LoginTakingAWhile', () => ({
                auth: { status: 'syncing', takingAWhile: true },
            }))

            logger.warn('login taking awhile')
        })

        // If the user is already logged in, they might have been made
        // to relog because an expired token. If so, just use the data
        // that is already on the machine
        const customer = DS_getData().customer
        const oldUser = LS_userGet()
        const oldCustomer = LS_customerGet()

        const { user } = await Auth_syncUserData(customer)

        await Translations.changeLanguage(user.language)

        if (oldUser != null && oldCustomer != null && user.id === oldUser.id && oldCustomer.id === customer.id) {
            await loginComplete(user, bus)
            return
        }

        const { accessToken } = DS_getData()

        // If the user has data on their machine already we
        // want to clear it out before writing in the new data.
        // This might be because they have closed the app during
        // an initial login sync before
        await Auth_cleanse()

        // We need to put the login data back in datastore after
        // cleansing everything
        DS_setData('LoginDataReset', () => ({
            accessToken,
            auth: { status: 'syncing' },
            customer,
            user,
        }))

        // Sync down feature flags first so they can be relied upon when deciding what endpoint api to use
        await Cust_sync()

        // Start some fetches that will be needed by various branches
        const syncP = retry(4, () => Serv_send('sync.v2', { since: 0, shouldFilterUnitListItemsByDate: false }))
        const formsP = retry(4, () => Serv_send('forms.getAll', { since: 0, shouldFilterQuestionsOnDate: false }))

        // serverPrefs
        // These need fetching before we call the Cust.hasSingleLocation() function
        await retry(4, Cust_serverPrefsFetch).then((serverPrefs) => {
            applyTheme(serverPrefs.theme)
            LS_serverPrefsSet(serverPrefs)
            DS_setData('ServerPreferencesFetched', () => ({ serverPrefs }))
        })

        const locationsP = syncP.then(({ result }) =>
            IDB_locationsWrite(
                (result.locations || []).map((locationDto) => ({
                    ...locationDto,
                    locationId: locationDto.id,
                }))
            )
        )

        // Parallel sync branches
        await Promise.all([
            // Fixes
            retry(4, () => Serv_send('buzz.getFixes', { since: 0 })).then(({ fixes }) => IDB_fixesWrite(fixes || [])),

            locationsP,

            // Sync forms
            locationsP.then(() => formsP.then(({ forms }) => Form_sync(forms as any))),

            // syncP dependants
            syncP.then(async ({ result }) => {
                await IDB_locationFocusListsWrite(
                    (result.locationFocusLists || []).map((focusList) => ({
                        ...focusList,
                        locationFocusListId: focusList.id,
                    }))
                )
                await IDB_issuesWrite(result.issues || [])
                await IDB_smartDataOrderingsWrite(result.smartDataOrdering || [])
                await IDB_smartDatasWrite(SD_extractSmartDataItems(result.smartData || []))
                await IDB_smartDataReferencesWrite(SD_extractTargetReferences(result.smartData || [], []))
                await IDB_actionTypesWrite(result.actions || [])
                await IDB_unitsWrite(result.units || [])
                await IDB_unitTypesWrite(result.unitTypes || [])
                await IDB_unitListsWrite(result.unitLists || [])
                await IDB_proceduresWrite(result.campaigns || [])
                await IDB_reportsWrite(
                    logger,
                    (result.reports || []).map((report) => ({
                        ...report,
                        onlineId: report.id,
                        uploaded: true,
                        items: report.items.map((item) => ({
                            ...item,
                            images: item.images || [],
                            notes: item.notes || '',
                        })),
                    }))
                )
                if (!Cust_hasSingleLocation()) {
                    await Loca_fetchTimelineEvents(
                        globalHive,
                        [],
                        (result.locations || []).map((l) => l.id),
                        Loca_updateCurrentTimeline
                    )
                }
                await I18N_sync(result.language)

                // pf sync is dependant on slugs having synced (img caching of slugified urls)
                await Sync_syncProcessFlow(0)
            }),

            // Calendar events
            !Cust_hasSingleLocation() &&
                retry(4, () => Serv_send('v2.calendar.get', {})).then(({ events }) =>
                    IDB_calendarEventsWrite(
                        (events || []).map((event) => ({
                            ...event,
                            onlineId: event.id,
                            author: event.author.id,
                        }))
                    )
                ),
            Cust_hasSingleLocation() && Post_sync({ requireUnitEventPosts: false }),
            Sync_syncCommon(logger),
            Cust_hasSingleLocation() && Todo_sync(),
        ])

        // Recover in-progress data from previous login
        await Devi_recoverInProgressData(customer.id, user.id)

        // If auth status change, do not complete
        if (Auth_getStatus() !== 'syncing') return

        // cache the images for units, only for field merchandising customers
        if (!Cust_hasSingleLocation()) {
            syncP.then(({ result }) => Imag_cacheImages(Imag_getImageUrlsForUnits(result.units)))
        }

        // Persist the last successful sync time
        const syncTime = new Date((await syncP).time).getTime()
        globalHive.setlastDBupdate('sync', Util_unixTime(syncTime))
        globalHive.dispatch(setLastSyncTime(syncTime))
        DS_setData('LoginSyncCompleted', () => ({ lastSyncTime: syncTime }))

        await loginComplete(user, bus)

        // Log the time to sync
        Logg_event('LoginComplete', { time: timer.getDurationInWholeSeconds() })
    } catch (error) {
        logger.error(error)

        // Explain that the login failed and that they should try again
        DS_setData('LoginSyncFailed', () => ({ auth: { status: 'failure' } }))
    }

    sentryTrnx.finish()
}

async function loginComplete(user: User_IUser, bus: Base_Bus) {
    // Persist only after everything has successfully synced
    LS_accessTokenSet(DS_getData().accessToken)
    LS_customerSet(DS_getData().customer)
    LS_userSet(user)

    // Trigger reading all entities from IDB
    // (could set them directly)
    globalHive.dispatch(syncAll())

    Acti_initialise(globalHive)

    // Navigate to new screen
    await Rout_toHome(true)

    // Start the sync going again
    Sync_reset(bus)

    // Setup inactivity checking
    IDB_lastActivity().set(new Date())
    Devi_inactivityCheck()

    await Noti_subscribe()

    // Dispatch completed status
    DS_setData('LoginComplete', () => ({ auth: { status: 'complete' } }))

    E2E_dispatchSyncSuccess()

    // Zendesk Chat
    ZendeskWebChat.hideChat()
    ZendeskWebChat.setLocale(user.language)
}

export async function Auth_syncUserData(customer: DS_TData['customer']) {
    const user = await retry(4, User_fetch)
    const profileImageUrl = UserProfileImageCache.getCachedBlobUrl(user.profileInfo.image)
    user.profileImageUrl = profileImageUrl

    DS_setData('UserFetched', () => ({ user }))
    LS_userSet(user)

    sentry.userSet(user.id, user.email)
    I18N_setLocale(user.language)
    Auth_pendoIdentify(customer, user)

    await LaunchDarkly.initialize({
        name: user.name,
        customerSlug: customer.slug,
        email: user.email,
        language: user.language,
        isZapticStaff: user.zapticStaff,
    })

    await updateLastActiveTime(user)
    return { user }
}
